import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { CheckCircle2, XCircle, Clock, AlertTriangle, FileText, Users } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABEL = {
  pending_manager: { label: 'Pending', color: 'bg-amber-500/10 text-amber-600 border-amber-500/30' },
  manager_approved: { label: 'Manager Approved', color: 'bg-blue-500/10 text-blue-600 border-blue-500/30' },
  manager_rejected: { label: 'Rejected', color: 'bg-red-500/10 text-red-600 border-red-500/30' },
  hr_approved: { label: 'HR Approved', color: 'bg-green-500/10 text-green-700 border-green-500/30' },
  hr_rejected: { label: 'HR Rejected', color: 'bg-red-500/10 text-red-600 border-red-500/30' },
};

function todayStr() {
  return new Date().toISOString().split('T')[0];
}

export default function TimesheetTeamPage() {
  const [date, setDate] = useState(todayStr());
  const [filterStatus, setFilterStatus] = useState('');
  const [data, setData] = useState({ items: [], not_submitted_today: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [actionTs, setActionTs] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (date) params.set('date', date);
      if (filterStatus) params.set('status', filterStatus);
      const res = await api.get(`/sshr/timesheet/v2/team?${params.toString()}`);
      setData(res.data || { items: [], not_submitted_today: [], summary: {} });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load team timesheets');
      setData({ items: [], not_submitted_today: [], summary: {} });
    } finally {
      setLoading(false);
    }
  }, [date, filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAction = (ts, type) => {
    setActionTs(ts);
    setActionType(type);
    setComment('');
  };

  const submitAction = async () => {
    if (actionType === 'reject' && !comment.trim()) {
      toast.error('Rejection requires a comment');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/sshr/timesheet/v2/${actionTs.id}/manager-action`, {
        action: actionType,
        comment: comment.trim(),
      });
      toast.success(`Timesheet ${actionType}d`);
      setActionTs(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  const grouped = useMemo(() => {
    const buckets = { pending_manager: [], manager_approved: [], hr_approved: [], rejected: [] };
    (data.items || []).forEach(it => {
      if (it.status === 'pending_manager') buckets.pending_manager.push(it);
      else if (it.status === 'manager_approved') buckets.manager_approved.push(it);
      else if (it.status === 'hr_approved') buckets.hr_approved.push(it);
      else buckets.rejected.push(it);
    });
    return buckets;
  }, [data]);

  const renderRow = (ts) => {
    const s = STATUS_LABEL[ts.status] || { label: ts.status, color: 'bg-muted' };
    return (
      <div key={ts.id} className="border rounded-lg p-3 space-y-2 hover:bg-muted/20" data-testid={`team-ts-row-${ts.id}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="font-medium">{ts.employee_name}</p>
            <p className="text-xs text-muted-foreground">
              {ts.date} · {ts.total_hours}h · {(ts.rows || []).length} tasks
              {ts.is_late && <span className="text-amber-600 ml-1">· LATE</span>}
              {ts.has_other_tasks && <span className="text-purple-600 ml-1">· has &quot;Other&quot;</span>}
            </p>
          </div>
          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${s.color}`}>{s.label}</span>
        </div>
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">View {(ts.rows || []).length} task entries</summary>
          <div className="mt-2 space-y-1.5 pl-2 border-l-2 border-muted">
            {(ts.rows || []).map((r, i) => (
              <div key={i}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{r.task_name}</span>
                  <Badge variant="outline">{r.hours}h</Badge>
                </div>
                <p className="text-muted-foreground whitespace-pre-wrap">{r.description}</p>
              </div>
            ))}
            {ts.is_late && ts.late_reason && (
              <p className="text-amber-700 mt-2"><strong>Late reason:</strong> {ts.late_reason}</p>
            )}
            {ts.manager_comment && (
              <p className="text-blue-700 mt-2"><strong>Your note:</strong> {ts.manager_comment}</p>
            )}
          </div>
        </details>
        {ts.status === 'pending_manager' && (
          <div className="flex gap-2 justify-end pt-1">
            <Button
              size="sm"
              variant="outline"
              className="text-red-600 border-red-500/30 hover:bg-red-500/10"
              onClick={() => openAction(ts, 'reject')}
              data-testid={`team-reject-${ts.id}`}
            >
              <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
            </Button>
            <Button
              size="sm"
              onClick={() => openAction(ts, 'approve')}
              data-testid={`team-approve-${ts.id}`}
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Approve
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="timesheet-team-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Team Timesheets</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Review and approve daily timesheets from your direct reports.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-[160px]"
            data-testid="team-date-input"
          />
          <Button variant="outline" size="sm" onClick={() => { setDate(''); }} data-testid="team-clear-date">
            All Dates
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Submitted Today</p>
          <p className="text-2xl font-bold">{data.summary?.submitted || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Pending Review</p>
          <p className="text-2xl font-bold text-amber-600">{data.summary?.pending_review || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Awaiting HR</p>
          <p className="text-2xl font-bold text-blue-600">{data.summary?.manager_approved || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Not Submitted</p>
          <p className="text-2xl font-bold text-red-600">{(data.not_submitted_today || []).length}</p>
        </CardContent></Card>
      </div>

      {(data.not_submitted_today || []).length > 0 && (
        <Card className="border-red-500/30 bg-red-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-4 w-4" /> Missing Today ({date || 'today'})
            </CardTitle>
            <CardDescription>These reports haven&apos;t submitted a timesheet yet</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data.not_submitted_today.map(e => (
                <Badge key={e.id} variant="outline" className="border-red-500/30 text-red-600">
                  {e.full_name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Submissions</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="pending_manager">
            <TabsList>
              <TabsTrigger value="pending_manager" data-testid="tab-pending">
                Pending ({grouped.pending_manager.length})
              </TabsTrigger>
              <TabsTrigger value="manager_approved" data-testid="tab-approved">
                Approved ({grouped.manager_approved.length})
              </TabsTrigger>
              <TabsTrigger value="hr_approved" data-testid="tab-hr-approved">
                HR Locked ({grouped.hr_approved.length})
              </TabsTrigger>
              <TabsTrigger value="rejected" data-testid="tab-rejected">
                Rejected ({grouped.rejected.length})
              </TabsTrigger>
            </TabsList>
            {Object.entries(grouped).map(([k, items]) => (
              <TabsContent key={k} value={k} className="space-y-2 mt-3">
                {loading ? (
                  <div className="py-10 flex justify-center">
                    <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
                  </div>
                ) : items.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">No timesheets in this bucket</p>
                ) : items.map(renderRow)}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      <Dialog open={!!actionTs} onOpenChange={(o) => !o && setActionTs(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'approve' ? 'Approve' : 'Reject'} Timesheet —{' '}
              {actionTs?.employee_name} ({actionTs?.date})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {actionType === 'approve'
                ? 'This will forward the timesheet to HR for final approval.'
                : 'Rejection sends the timesheet back to the employee. They must correct & resubmit.'}
            </p>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={actionType === 'reject' ? 'Reason for rejection (required)' : 'Optional note'}
              className="min-h-[80px]"
              data-testid="team-action-comment"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionTs(null)}>Cancel</Button>
            <Button
              onClick={submitAction}
              disabled={submitting}
              variant={actionType === 'reject' ? 'destructive' : 'default'}
              data-testid="team-action-submit"
            >
              {submitting ? 'Saving…' : (actionType === 'approve' ? 'Approve' : 'Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
